import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ITALIAN_PROVINCE } from 'receivable-invoices-js/react/constants/it-provinces';

interface ErrorItem {
  message: string;
}

interface UseDetailsDataProps {
  address?: string;
  readonlyAddress?: string;
  zipcode?: string;
  readonlyZipcode?: string;
  city?: string;
  readonlyCity?: string;
  readonlyCountry?: string;
  addressErrors?: ErrorItem[];
  zipcodeErrors?: ErrorItem[];
  cityErrors?: ErrorItem[];
  countryErrors?: ErrorItem[];
  ibanErrors?: ErrorItem[];
  provinceErrors?: ErrorItem[];
}

interface ProvinceOption {
  key: string;
  label: string;
}

export function useDetailsData({
  address,
  readonlyAddress,
  zipcode,
  readonlyZipcode,
  city,
  readonlyCity,
  readonlyCountry,
  addressErrors,
  zipcodeErrors,
  cityErrors,
  countryErrors,
  ibanErrors,
  provinceErrors,
}: UseDetailsDataProps) {
  const { formatMessage } = useIntl();

  const isReadOnly = useCallback(
    (readonlyField: string) => {
      const readonlyProps: Record<string, string | undefined> = {
        readonlyAddress,
        readonlyZipcode,
        readonlyCity,
        readonlyCountry,
      };
      return readonlyProps[readonlyField] !== undefined;
    },
    [readonlyAddress, readonlyCity, readonlyCountry, readonlyZipcode]
  );

  const getValue = useCallback(
    (field: string, readonlyValue?: string, localCountry?: string) => {
      const readonlyField = `readonly${field.charAt(0).toUpperCase()}${field.slice(1)}`;
      if (isReadOnly(readonlyField)) {
        return readonlyValue;
      }

      const fieldValues: Record<string, string | undefined> = {
        address,
        zipcode,
        city,
        country: localCountry,
      };
      return fieldValues[field];
    },
    [address, zipcode, city, isReadOnly]
  );

  const provinceOptions = useMemo((): ProvinceOption[] => {
    return ITALIAN_PROVINCE.map(key => ({
      key,
      label: `${key} - ${formatMessage({ id: `italy.provinces.${key.toLowerCase()}` })}`,
    }));
  }, [formatMessage]);

  const getErrorMessage = useCallback(
    (field: string, code: string): string => {
      const ERRORS_BY_FIELD: Record<string, Record<string, string>> = {
        address: {
          required: formatMessage({ id: 'validations.errors.blank' }),
        },
        zipcode: {
          required: formatMessage({ id: 'validations.errors.blank' }),
          len: formatMessage({ id: 'validations.errors.sdd.zipcode' }),
        },
        city: {
          required: formatMessage({ id: 'validations.errors.blank' }),
        },
        country: {
          required: formatMessage({ id: 'validations.errors.blank' }),
        },
        iban: {
          required: formatMessage({ id: 'validations.errors.blank' }),
          invalid_iban: formatMessage({ id: 'validations.errors.sdd.iban-not-valid' }),
          sdd_disabled: formatMessage({ id: 'validations.errors.sdd.iban-not-supported' }),
        },
        province: {
          required: formatMessage({ id: 'validations.errors.blank' }),
        },
      };

      return ERRORS_BY_FIELD[field]?.[code] || 'Invalid value';
    },
    [formatMessage]
  );

  const getError = useCallback((errors?: ErrorItem[]) => {
    return errors && errors.length > 0 ? errors[0]?.message : undefined;
  }, []);

  const addressError = getError(addressErrors);
  const zipcodeError = getError(zipcodeErrors);
  const cityError = getError(cityErrors);
  const countryError = getError(countryErrors);
  const ibanError = getError(ibanErrors);
  const provinceError = getError(provinceErrors);

  return {
    isReadOnly,
    getValue,
    getErrorMessage,
    provinceOptions,
    errors: {
      address: addressError,
      zipcode: zipcodeError,
      city: cityError,
      country: countryError,
      iban: ibanError,
      province: provinceError,
    },
  };
}
